/**
 * API Service pentru Devices (Aparate medicale)
 */

import apiClient from './api';
import {
  DeviceType,
  Device,
  DeviceListItem,
  DevicesListResponse,
  DevicesFilters,
  DeviceCreate,
  DeviceUpdate,
  DeviceStatusChange,
  DeviceSchedule,
  DeviceScheduleCreate,
  DeviceException,
  DeviceExceptionCreate,
} from '../types/devices';

// ============================================================
// Device Types (readonly seed data)
// ============================================================

export const getDeviceTypes = async (category?: string, isActive: boolean = true): Promise<DeviceType[]> => {
  const params = new URLSearchParams();
  if (category) params.append('category', category);
  params.append('is_active', String(isActive));
  
  const response = await apiClient.get(`/devices/types?${params.toString()}`);
  return response.data;
};

export const getDeviceType = async (id: number): Promise<DeviceType> => {
  const response = await apiClient.get(`/devices/types/${id}`);
  return response.data;
};

// ============================================================
// Devices CRUD
// ============================================================

export const getDevices = async (filters: DevicesFilters = {}): Promise<DevicesListResponse> => {
  const params = new URLSearchParams();
  
  if (filters.institutionId) params.append('institutionId', filters.institutionId);
  if (filters.deviceTypeId) params.append('deviceTypeId', String(filters.deviceTypeId));
  if (filters.status) params.append('status', filters.status);
  if (filters.search) params.append('search', filters.search);
  if (filters.skip !== undefined) params.append('skip', String(filters.skip));
  if (filters.limit !== undefined) params.append('limit', String(filters.limit));
  
  const response = await apiClient.get(`/devices?${params.toString()}`);
  return response.data;
};

export const getDevice = async (id: string): Promise<Device> => {
  const response = await apiClient.get(`/devices/${id}`);
  return response.data;
};

export const createDevice = async (data: DeviceCreate): Promise<Device> => {
  const response = await apiClient.post('/devices', data);
  return response.data;
};

export const updateDevice = async (id: string, data: DeviceUpdate): Promise<Device> => {
  const response = await apiClient.patch(`/devices/${id}`, data);
  return response.data;
};

export const deleteDevice = async (id: string): Promise<void> => {
  await apiClient.delete(`/devices/${id}`);
};

export const changeDeviceStatus = async (id: string, data: DeviceStatusChange): Promise<Device> => {
  const response = await apiClient.post(`/devices/${id}/status`, data);
  return response.data;
};

// ============================================================
// Device Schedule
// ============================================================

export const getDeviceSchedule = async (deviceId: string): Promise<DeviceSchedule[]> => {
  const response = await apiClient.get(`/devices/${deviceId}/schedule`);
  return response.data;
};

export const updateDeviceSchedule = async (
  deviceId: string,
  schedules: DeviceScheduleCreate[]
): Promise<DeviceSchedule[]> => {
  const response = await apiClient.put(`/devices/${deviceId}/schedule`, { schedules });
  return response.data;
};

// ============================================================
// Device Exceptions
// ============================================================

export const getDeviceExceptions = async (
  deviceId: string,
  dateFrom?: string,
  dateTo?: string,
  exceptionType?: string
): Promise<DeviceException[]> => {
  const params = new URLSearchParams();
  if (dateFrom) params.append('dateFrom', dateFrom);
  if (dateTo) params.append('dateTo', dateTo);
  if (exceptionType) params.append('exceptionType', exceptionType);
  
  const response = await apiClient.get(`/devices/${deviceId}/exceptions?${params.toString()}`);
  return response.data;
};

export const createDeviceException = async (
  deviceId: string,
  data: DeviceExceptionCreate
): Promise<DeviceException> => {
  const response = await apiClient.post(`/devices/${deviceId}/exceptions`, data);
  return response.data;
};

export const deleteDeviceException = async (deviceId: string, exceptionId: string): Promise<void> => {
  await apiClient.delete(`/devices/${deviceId}/exceptions/${exceptionId}`);
};

// Export all functions
export default {
  getDeviceTypes,
  getDeviceType,
  getDevices,
  getDevice,
  createDevice,
  updateDevice,
  deleteDevice,
  changeDeviceStatus,
  getDeviceSchedule,
  updateDeviceSchedule,
  getDeviceExceptions,
  createDeviceException,
  deleteDeviceException,
};
